import axios from 'axios'
import { isEmpty } from 'lodash'
import { appendQueryParams } from './UrlHelper'
import apiConfig from '../config/apiConfig'

import { setDownloadAssetInProgress } from '../store/search/actions'
import {
  DOWNLOAD_JPEG_VERSION,
  DOWNLOAD_ORIGINAL_VERSION,
} from '../constants/search'
import { isEmptyObject, Search } from './UtilityHelper'
import { trackBetaSearchBulkDownloads } from '../analytics/betaSearch'

const {
  key: KEY = '',
  metadata: { assetDownloadMetricUrl = '' } = {},
  assethub: { createZipUrl = '', zipJobsUrl = '' } = {},
} = apiConfig

let totalDownloads = 0
let initiatedDownloads = 0
let remainingAssets = {}
let remainingFiles = []

export const getAssetDownloadFileType = (
  asset = {},
  fallbackExtension = '',
) => {
  const rawType = String(
    asset?.fileType ||
      asset?.file_type ||
      asset?.asset_content_type ||
      asset?.assetContentType ||
      fallbackExtension ||
      '',
  ).trim()

  if (!rawType) {
    return ''
  }

  const normalizedType = rawType
    .toLowerCase()
    .split(';')[0]
    .split('/').pop()
    .split('?')[0]
    .split('#')[0]
    .replace(/^\./, '')
    .trim()

  if (!normalizedType || ['unknown', 'application/octet-stream'].includes(normalizedType)) {
    return ''
  }

  return normalizedType
}

export const normalizeDownloadFilename = (
  fileName = '',
  fileType = '',
  fallbackExtension = '',
) => {
  const trimmedName = `${fileName ?? ''}`.trim()
  if (!trimmedName) {
    return trimmedName
  }

  const extensionIndex = trimmedName.lastIndexOf('.')
  if (extensionIndex > 0 && extensionIndex < trimmedName.length - 1) {
    const baseName = trimmedName.substring(0, extensionIndex)
    const normalizedExtension = trimmedName
      .substring(extensionIndex + 1)
      .toLowerCase()

    return `${baseName}.${normalizedExtension}`
  }

  const resolvedType = getAssetDownloadFileType(
    { fileType, asset_content_type: fileType },
    fallbackExtension,
  )

  if (!resolvedType) {
    return trimmedName
  }

  return `${trimmedName}.${resolvedType}`
}

export const startDownload = (
  downloadAssets = {},
  dispatch,
  fileType,
  auth = {},
  page,
  embededQueryParams = false,
  location = '',
) => {
  const { lanId = '' } = auth
  let downloadAssetKeys = Object.keys(downloadAssets)
  let downloadedAssets = []
  for (let i = 0; i < downloadAssetKeys.length; i++) {
    const downloadAssetDetails = downloadAssets[downloadAssetKeys[i]]
    downloadedAssets.push(downloadAssetDetails)
  }
  totalDownloads = Object.keys(downloadAssets).length
  dispatch(setDownloadAssetInProgress(true))
  const betaFireflyData = {
    selectedAssets: downloadedAssets,
    downloadAssetType: fileType,
    auth,
    page,
    location,
  }
  downloadAllAssets(
    downloadAssets,
    dispatch,
    fileType,
    lanId,
    embededQueryParams,
    betaFireflyData
  )
}

const downloadAsset = async (
  {
    fileName = '',
    fileType: fileExtension = '',
    filePath = '',
    fileSizeMb = '',
    render_urls = {},
    assetId = '',
  },
  dispatch,
  fileType = DOWNLOAD_ORIGINAL_VERSION,
  userId,
  embededQueryParams = false,
  fireflyData = {},
) => {
  let downloadFileName = normalizeDownloadFilename(fileName, fileExtension)
  const { original_asset_url = '', optimized_asset_url = '' } = render_urls
  // adding logic to downlaod jpeg version
  let modifiedFilePath = original_asset_url
  if (fileType === DOWNLOAD_JPEG_VERSION) {
    const baseFileName = fileName.includes('.')
      ? fileName.substring(0, fileName.lastIndexOf('.'))
      : fileName
    downloadFileName = `${baseFileName}.jpeg`
    modifiedFilePath = optimized_asset_url
  }
  const imageQueryParams = {
    file_name: downloadFileName,
    is_download: true,
    key: KEY,
  }
  const downloadFilePath = appendQueryParams(modifiedFilePath, imageQueryParams)

  const blobResponse = await downloadBulkFileAPICall(downloadFilePath)
  if (blobResponse) {
    if (downloadFilePath && !downloadFilePath.includes('streaming')) {
      updateDownloadMetricsByAssetId(assetId)
    }
    downloadBulk(blobResponse, downloadFileName, fileExtension)
  }
}

export function downloadBulkFileAPICall(downloadFilePath = '') {
  return axios.get(downloadFilePath, {
    responseType: 'blob',
  })
}

export function downloadBulk(
  response = {},
  downloadFileName = '',
  fileExtension = '',
) {
  const { data = {} } = response
  const downloadableFilename = normalizeDownloadFilename(
    downloadFileName,
    fileExtension,
  )
  const downloadUrl = window.URL.createObjectURL(new Blob([data]))
  const downloadLink = document.createElement('a')
  downloadLink.href = downloadUrl
  downloadLink.setAttribute('download', downloadableFilename)
  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
  initiatedDownloads++
}

export const downloadAllAssets = async (
  downloadAssets = {},
  dispatch,
  fileType,
  userId,
  embededQueryParams,
  fireflyData = {},
) => {
  if (!isEmpty(downloadAssets)) {
    remainingAssets = downloadAssets
  }
  const remainingAssetKeys = Object.keys(remainingAssets)

  if (remainingAssetKeys.length > 0) {
    // Download all assets in parallel and track which ones succeed
    const downloadPromises = remainingAssetKeys.map((key) => {
      const downloadAssetDetails = remainingAssets[key]
      delete remainingAssets[key]
      return {
        promise: downloadAsset(
          { ...downloadAssetDetails },
          dispatch,
          fileType,
          userId,
          embededQueryParams,
          fireflyData,
        ),
        assetId: downloadAssetDetails.assetId,
      }
    })

    // Wait for all downloads to complete
    const results = await Promise.allSettled(
      downloadPromises.map((item) => item.promise),
    )

    // Filter out failed downloads from firefly tracking
    if (!isEmptyObject(fireflyData)) {
      const successfulAssetIds = new Set()
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successfulAssetIds.add(downloadPromises[index].assetId)
        }
      })

      // Filter selectedAssets to only include successful downloads
      const filteredFireflyData = {
        ...fireflyData,
        selectedAssets: fireflyData?.selectedAssets?.filter((asset) =>
          successfulAssetIds.has(asset.assetId),
        ),
      }

      if (filteredFireflyData?.selectedAssets?.length > 0) {
        trackBetaSearchBulkDownloads(filteredFireflyData)
      }
    }
  }

  // Only trigger completion tracking once all downloads are done
  if (dispatch) {
    dispatch(setDownloadAssetInProgress(false))
  }
}

/**
 * Beta Search Download Methods Start
 */
const downloadAssetFile = async (
  {
    fileName = '',
    fileType: fileExtension = '',
    render_urls = {},
    assetId = '',
  },
  dispatch,
  fileType = DOWNLOAD_ORIGINAL_VERSION,
  userId,
  embededQueryParams = false,
) => {
  let downloadFileName = normalizeDownloadFilename(fileName, fileExtension)
  const { originalAssetUrl = '', optimizedAssetUrl = '' } = render_urls
  // adding logic to downlaod jpeg version
  let modifiedFilePath = originalAssetUrl
  if (fileType === DOWNLOAD_JPEG_VERSION) {
    const baseFileName = fileName.includes('.')
      ? fileName.substring(0, fileName.lastIndexOf('.'))
      : fileName
    downloadFileName = `${baseFileName}.jpeg`
    modifiedFilePath = optimizedAssetUrl
  }
  const imageQueryParams = {
    file_name: downloadFileName,
    is_download: true,
    key: KEY,
  }
  const downloadFilePath = appendQueryParams(modifiedFilePath, imageQueryParams)

  const blobResponse = await downloadBulkFileAPICall(downloadFilePath)
  if (blobResponse) {
    if (downloadFilePath && !downloadFilePath.includes('streaming')) {
      updateDownloadMetricsByAssetId(assetId)
    }
    downloadBulk(blobResponse, downloadFileName, fileExtension)
  }
}

export const chunk = (arr, size) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
    arr.slice(i * size, i * size + size),
  )

export const downloadAllFiles = (
  downloadAssets = [],
  dispatch,
  fileType,
  userId,
  embededQueryParams,
  betaFireflyData = {},
) => {
  if (!isEmpty(downloadAssets)) {
    remainingFiles = downloadAssets
  }
  const downloadFileChunks = downloadAssets ? chunk(downloadAssets, 5) : []
  let remainingChunksCount = downloadFileChunks?.length

  for (let i = 0; i < downloadFileChunks?.length; i++) {
    const currentFilesList = downloadFileChunks[i] || []
    currentFilesList?.map((currentAsset = {}) => {
      downloadAssetFile(
        { ...currentAsset },
        dispatch,
        fileType,
        userId,
        embededQueryParams,
      )
      return true
    })
    remainingChunksCount--
  }

  if (remainingChunksCount === Number(0)) {
    dispatch(setDownloadAssetInProgress(false))
    if (!isEmptyObject(betaFireflyData)) {
      trackBetaSearchBulkDownloads(betaFireflyData)
    }
    dispatch({
      type: 'search/saveSelectedItems',
      payload: { selectedItems: [] },
    })
    return true
  }
}

export const startDownloadFile = ({
  selectedAssets = [],
  dispatch = () => { },
  downloadAssetType = '',
  auth = {},
  page = Number(0),
  location = Search,
  embededQueryParams = false,
}) => {
  const { lanId = '' } = auth
  totalDownloads = selectedAssets.length
  dispatch(setDownloadAssetInProgress(true))
  const betaFireflyData = {
    selectedAssets,
    downloadAssetType,
    auth,
    page,
    location,
  }
  downloadAllFiles(
    selectedAssets,
    dispatch,
    downloadAssetType,
    lanId,
    embededQueryParams,
    betaFireflyData,
  )
}

export const getAssetIdFromVaultDigitalUrl = (url) => {
  const urlObj = new URL(url)
  const pathname = urlObj.pathname
  const parts = pathname.split('/')
  return parts[parts.length - 1]
}

export const updateDownloadMetricsByUrl = (url = '') => {
  const assetId = getAssetIdFromVaultDigitalUrl(url)
  axios.post(`${assetDownloadMetricUrl.replace(':assetId', assetId)}`)
}

export const updateDownloadMetricsByAssetId = (assetId = '') => {
  axios.post(`${assetDownloadMetricUrl.replace(':assetId', assetId)}`)
}

export const createZipDownload = (payload = {}) => axios.post(createZipUrl, payload)

export const fetchAllZipJobsSummary = () => axios.get(`${zipJobsUrl}?pageNo=0&size=100&sortBy=createdAt`)
